import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  LayoutDashboard, Users, Package, FileText, BarChart3, Settings,
  Bell, Search, ChevronRight, TrendingUp, TrendingDown, DollarSign,
  ShoppingCart, Activity, ArrowLeft, Play, X, CheckCircle,
  Eye, MoreHorizontal, Filter, Download, Plus, Zap, Loader2, Rocket, Send,
  Menu
} from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from "recharts";
import { LogoIcon } from "@/components/LogoWithText";
import ScrollProgress from "@/components/effects/ScrollProgress";
import axios from "axios";
import { toast } from "sonner";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── MOCK DATA ───
const revenueData = [
  { mes: "Sep", ingresos: 42000, gastos: 28000 },
  { mes: "Oct", ingresos: 58000, gastos: 31000 },
  { mes: "Nov", ingresos: 51000, gastos: 29000 },
  { mes: "Dic", ingresos: 73000, gastos: 35000 },
  { mes: "Ene", ingresos: 89000, gastos: 38000 },
  { mes: "Feb", ingresos: 127450, gastos: 42000 },
];

const sectorData = [
  { name: "Logística", value: 35, color: "#3b82f6" },
  { name: "Comercio", value: 25, color: "#06b6d4" },
  { name: "Finanzas", value: 20, color: "#8b5cf6" },
  { name: "Salud", value: 12, color: "#10b981" },
  { name: "Otros", value: 8, color: "#f59e0b" },
];

const weeklyOrders = [
  { dia: "Lun", pedidos: 45 }, { dia: "Mar", pedidos: 62 },
  { dia: "Mié", pedidos: 58 }, { dia: "Jue", pedidos: 73 },
  { dia: "Vie", pedidos: 89 }, { dia: "Sáb", pedidos: 34 },
  { dia: "Dom", pedidos: 12 },
];

const clients = [
  { id: 1, name: "Logística Express S.A.", contact: "Carlos Rodríguez", email: "carlos@logex.com", sector: "Logística", status: "Activo", revenue: "$12,400" },
  { id: 2, name: "TechRetail Solutions", contact: "María González", email: "maria@techretail.com", sector: "Comercio", status: "Activo", revenue: "$8,750" },
  { id: 3, name: "Finanzas Plus Corp", contact: "Roberto Sánchez", email: "roberto@finplus.com", sector: "Finanzas", status: "Activo", revenue: "$15,200" },
  { id: 4, name: "MediSalud Internacional", contact: "Ana Martínez", email: "ana@medisalud.com", sector: "Salud", status: "Nuevo", revenue: "$3,100" },
  { id: 5, name: "Distribuidora Nacional", contact: "Jorge López", email: "jorge@distnac.com", sector: "Logística", status: "Activo", revenue: "$9,800" },
  { id: 6, name: "EcoFarm Orgánicos", contact: "Laura Ruiz", email: "laura@ecofarm.com", sector: "Comercio", status: "Pendiente", revenue: "$5,400" },
  { id: 7, name: "Construcciones Modernas", contact: "Pedro Hernández", email: "pedro@construmod.com", sector: "Otros", status: "Activo", revenue: "$7,600" },
  { id: 8, name: "AutoParts Premium", contact: "Diana Torres", email: "diana@autoparts.com", sector: "Comercio", status: "Activo", revenue: "$11,300" },
];

const products = [
  { id: 1, name: "Módulo ERP Core", stock: 87, total: 100, price: "$2,499", category: "Software", trend: "up" },
  { id: 2, name: "Suite Facturación", stock: 64, total: 100, price: "$899", category: "Facturación", trend: "up" },
  { id: 3, name: "Módulo RRHH", stock: 42, total: 80, price: "$1,299", category: "Recursos", trend: "down" },
  { id: 4, name: "Analytics Dashboard", stock: 93, total: 100, price: "$1,799", category: "Reportes", trend: "up" },
  { id: 5, name: "CRM Avanzado", stock: 28, total: 60, price: "$1,599", category: "Clientes", trend: "up" },
  { id: 6, name: "Módulo Inventarios", stock: 15, total: 50, price: "$999", category: "Inventario", trend: "down" },
];

const notifications = [
  { id: 1, text: "Nuevo cliente registrado: MediSalud", time: "Hace 5 min", type: "success" },
  { id: 2, text: "Pedido #4521 completado", time: "Hace 12 min", type: "info" },
  { id: 3, text: "Alerta: Stock bajo en Módulo Inventarios", time: "Hace 30 min", type: "warning" },
  { id: 4, text: "Factura #892 pagada - $8,750", time: "Hace 1h", type: "success" },
  { id: 5, text: "Actualización del sistema disponible v3.2", time: "Hace 2h", type: "info" },
];

const recentActivity = [
  { id: 1, action: "Factura generada", detail: "#FA-2024-892 para TechRetail", time: "10:42", icon: FileText },
  { id: 2, action: "Cliente actualizado", detail: "Logística Express - Plan Enterprise", time: "10:15", icon: Users },
  { id: 3, action: "Pago recibido", detail: "$15,200 de Finanzas Plus Corp", time: "09:58", icon: DollarSign },
  { id: 4, action: "Pedido creado", detail: "#PED-4521 - Suite Facturación", time: "09:30", icon: ShoppingCart },
  { id: 5, action: "Reporte exportado", detail: "Ventas Q1 2026 - PDF", time: "09:05", icon: Download },
];

const tourSteps = [
  { target: "kpi-cards", title: "Panel de Control", desc: "Visualiza tus métricas clave en tiempo real: ingresos, clientes, pedidos y eficiencia." },
  { target: "revenue-chart", title: "Gráfica de Ingresos", desc: "Analiza tendencias de ingresos vs gastos con gráficas interactivas y datos históricos." },
  { target: "sector-chart", title: "Distribución por Sector", desc: "Conoce cómo se distribuyen tus clientes por industria para tomar decisiones estratégicas." },
  { target: "activity-feed", title: "Actividad Reciente", desc: "Seguimiento en tiempo real de todas las operaciones: facturas, pagos, pedidos." },
  { target: "sidebar-nav", title: "Navegación Intuitiva", desc: "Accede rápidamente a Clientes, Inventario, Facturación y Reportes desde el menú lateral." },
];

// ─── ANIMATED COUNTER ───
const AnimCounter = ({ value, prefix = "", suffix = "" }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const num = parseInt(String(value).replace(/[^0-9]/g, ""));
    let start = 0;
    const duration = 1500;
    const startTime = Date.now();
    const timer = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * num));
      if (progress >= 1) clearInterval(timer);
    }, 16);
    return () => clearInterval(timer);
  }, [value]);
  return <span>{prefix}{count.toLocaleString()}{suffix}</span>;
};

// ─── CUSTOM TOOLTIP ───
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0b1221] border border-white/10 p-3 rounded-sm backdrop-blur-sm">
      <p className="text-white text-sm font-medium mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.name} className="text-sm" style={{ color: p.color }}>
          {p.name}: ${(p.value / 1000).toFixed(1)}K
        </p>
      ))}
    </div>
  );
};

// ─── SIDEBAR ───
const sidebarTabs = [
  { id: "dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { id: "clients", icon: Users, label: "Clientes" },
  { id: "inventory", icon: Package, label: "Inventario" },
  { id: "invoices", icon: FileText, label: "Facturación" },
  { id: "reports", icon: BarChart3, label: "Reportes" },
  { id: "settings", icon: Settings, label: "Configuración" },
];

const SidebarContent = ({ activeTab, setActiveTab, onNavClick }) => (
  <>
    <Link to="/" className="flex items-center gap-3 p-5 border-b border-white/5">
      <LogoIcon size={36} />
      <div>
        <span className="text-white font-bold text-sm tracking-wide">GLOBAL <span className="text-[#3b82f6]">METAFORA</span></span>
        <p className="text-[#94a3b8] text-[10px] tracking-wider">SISTEMA OPERATIVO v3.2</p>
      </div>
    </Link>

    <nav className="flex-1 py-4 px-3 space-y-1">
      {sidebarTabs.map((tab) => (
        <motion.button
          key={tab.id}
          data-testid={`nav-tab-${tab.id}`}
          onClick={() => { setActiveTab(tab.id); onNavClick?.(); }}
          className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-medium transition-all ${
            activeTab === tab.id
              ? "bg-[#3b82f6]/10 text-[#3b82f6] border-l-2 border-[#3b82f6]"
              : "text-[#94a3b8] hover:text-white hover:bg-white/5"
          }`}
          whileHover={{ x: 4 }}
          whileTap={{ scale: 0.98 }}
        >
          <tab.icon className="w-5 h-5" />
          {tab.label}
          {activeTab === tab.id && (
            <ChevronRight className="w-4 h-4 ml-auto" />
          )}
        </motion.button>
      ))}
    </nav>

    <div className="p-4 m-3 bg-gradient-to-br from-[#3b82f6]/10 to-[#06b6d4]/10 border border-[#3b82f6]/20 rounded-sm">
      <p className="text-[#3b82f6] text-xs font-semibold mb-1">DEMO MODE</p>
      <p className="text-[#94a3b8] text-[11px] mb-3">Explora todas las funcionalidades</p>
      <Link to="/#precios" data-testid="demo-upgrade-btn">
        <motion.div
          className="bg-[#3b82f6] text-white text-xs font-semibold py-2 px-3 text-center cursor-pointer"
          whileHover={{ boxShadow: "0 0 20px rgba(59,130,246,0.4)" }}
        >
          Ver Planes
        </motion.div>
      </Link>
    </div>
  </>
);

const Sidebar = ({ activeTab, setActiveTab, mobileOpen, setMobileOpen }) => (
  <>
    {/* Desktop sidebar */}
    <motion.aside
      data-testid="sidebar-nav"
      initial={{ x: -80, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="hidden lg:flex w-64 bg-[#0a0f1a] border-r border-white/5 flex-col h-screen sticky top-0"
    >
      <SidebarContent activeTab={activeTab} setActiveTab={setActiveTab} />
    </motion.aside>

    {/* Mobile drawer overlay */}
    <AnimatePresence>
      {mobileOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
            onClick={() => setMobileOpen(false)}
            data-testid="sidebar-overlay"
          />
          <motion.aside
            initial={{ x: -280 }}
            animate={{ x: 0 }}
            exit={{ x: -280 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed left-0 top-0 w-[280px] bg-[#0a0f1a] border-r border-white/5 flex flex-col h-screen z-50 lg:hidden"
            data-testid="sidebar-mobile"
          >
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute top-4 right-4 text-[#94a3b8] hover:text-white z-10"
              data-testid="sidebar-close-btn"
            >
              <X className="w-5 h-5" />
            </button>
            <SidebarContent activeTab={activeTab} setActiveTab={setActiveTab} onNavClick={() => setMobileOpen(false)} />
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  </>
);

// ─── KPI CARD ───
const KpiCard = ({ title, value, prefix, suffix, change, positive, icon: Icon, color, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.5 }}
    className="glass-card p-3 sm:p-5 relative overflow-hidden group"
    whileHover={{ borderColor: `${color}60`, y: -2 }}
    data-testid={`kpi-${title.toLowerCase().replace(/\s/g, "-")}`}
  >
    <motion.div
      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity"
      style={{ background: `radial-gradient(circle at top right, ${color}08, transparent)` }}
    />
    <div className="flex items-start justify-between mb-2 sm:mb-3 relative z-10">
      <div className="min-w-0">
        <p className="text-[#94a3b8] text-[10px] sm:text-xs font-medium uppercase tracking-wider truncate">{title}</p>
        <p className="text-lg sm:text-2xl font-bold text-white mt-0.5 sm:mt-1">
          <AnimCounter value={value} prefix={prefix} suffix={suffix} />
        </p>
      </div>
      <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}15` }}>
        <Icon className="w-4 h-4 sm:w-5 sm:h-5" style={{ color }} />
      </div>
    </div>
    <div className={`flex items-center gap-1 text-[10px] sm:text-xs font-medium relative z-10 ${positive ? "text-emerald-400" : "text-red-400"}`}>
      {positive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {change} vs mes anterior
    </div>
  </motion.div>
);

// ─── DASHBOARD VIEW ───
const DashboardView = () => (
  <div className="space-y-4 sm:space-y-6">
    {/* KPIs */}
    <div data-testid="kpi-cards" className="grid grid-cols-2 sm:grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">
      <KpiCard title="Ingresos Mensuales" value="127450" prefix="$" change="+43.2%" positive icon={DollarSign} color="#3b82f6" delay={0.1} />
      <KpiCard title="Clientes Activos" value="342" change="+12.5%" positive icon={Users} color="#06b6d4" delay={0.2} />
      <KpiCard title="Pedidos del Mes" value="1247" change="+28.1%" positive icon={ShoppingCart} color="#8b5cf6" delay={0.3} />
      <KpiCard title="Eficiencia Sistema" value="98" suffix="%" change="+2.3%" positive icon={Activity} color="#10b981" delay={0.4} />
    </div>

    <div className="grid lg:grid-cols-3 gap-4 sm:gap-6">
      {/* Revenue Chart */}
      <motion.div
        data-testid="revenue-chart"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="lg:col-span-2 glass-card p-3 sm:p-5"
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-white font-semibold text-sm sm:text-base">Ingresos vs Gastos</h3>
            <p className="text-[#94a3b8] text-xs">Últimos 6 meses</p>
          </div>
          <div className="flex gap-3 sm:gap-4 text-xs">
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-[#3b82f6] rounded-full" />Ingresos</span>
            <span className="flex items-center gap-1.5 text-[#94a3b8]"><span className="w-2 h-2 bg-[#94a3b8]/50 rounded-full" />Gastos</span>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={revenueData}>
            <defs>
              <linearGradient id="colorIngresos" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="mes" stroke="#94a3b8" fontSize={11} tickLine={false} />
            <YAxis stroke="#94a3b8" fontSize={11} tickLine={false} tickFormatter={(v) => `$${v/1000}K`} width={48} />
            <Tooltip content={<CustomTooltip />} />
            <Area type="monotone" dataKey="ingresos" stroke="#3b82f6" strokeWidth={2} fill="url(#colorIngresos)" name="Ingresos" />
            <Area type="monotone" dataKey="gastos" stroke="#94a3b8" strokeWidth={1.5} fill="transparent" strokeDasharray="5 5" name="Gastos" />
          </AreaChart>
        </ResponsiveContainer>
      </motion.div>

      {/* Sector Pie Chart */}
      <motion.div
        data-testid="sector-chart"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="glass-card p-3 sm:p-5"
      >
        <h3 className="text-white font-semibold text-sm sm:text-base mb-1">Distribución por Sector</h3>
        <p className="text-[#94a3b8] text-xs mb-4">Clientes activos</p>
        <ResponsiveContainer width="100%" height={140}>
          <PieChart>
            <Pie data={sectorData} cx="50%" cy="50%" innerRadius={40} outerRadius={60} paddingAngle={3} dataKey="value">
              {sectorData.map((entry) => (
                <Cell key={entry.name} fill={entry.color} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="space-y-2 mt-2">
          {sectorData.map((s) => (
            <div key={s.name} className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
                <span className="text-[#94a3b8]">{s.name}</span>
              </span>
              <span className="text-white font-medium">{s.value}%</span>
            </div>
          ))}
        </div>
      </motion.div>
    </div>

    <div className="grid lg:grid-cols-3 gap-4 sm:gap-6">
      {/* Weekly orders bar chart */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="glass-card p-3 sm:p-5"
      >
        <h3 className="text-white font-semibold text-sm sm:text-base mb-1">Pedidos de la Semana</h3>
        <p className="text-[#94a3b8] text-xs mb-4">Total: 373 pedidos</p>
        <ResponsiveContainer width="100%" height={160}>
          <BarChart data={weeklyOrders}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="dia" stroke="#94a3b8" fontSize={11} tickLine={false} />
            <YAxis stroke="#94a3b8" fontSize={11} tickLine={false} width={30} />
            <Bar dataKey="pedidos" fill="#3b82f6" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </motion.div>

      {/* Activity Feed */}
      <motion.div
        data-testid="activity-feed"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="lg:col-span-2 glass-card p-3 sm:p-5"
      >
        <h3 className="text-white font-semibold text-sm sm:text-base mb-4">Actividad Reciente</h3>
        <div className="space-y-2 sm:space-y-3">
          {recentActivity.map((item, i) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.7 + i * 0.08 }}
              className="flex items-center gap-3 sm:gap-4 p-2 sm:p-3 rounded-sm hover:bg-white/5 transition-colors group"
            >
              <div className="w-8 h-8 sm:w-9 sm:h-9 bg-[#3b82f6]/10 flex items-center justify-center flex-shrink-0">
                <item.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#3b82f6]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-xs sm:text-sm font-medium">{item.action}</p>
                <p className="text-[#94a3b8] text-xs truncate">{item.detail}</p>
              </div>
              <span className="text-[#94a3b8] text-xs flex-shrink-0">{item.time}</span>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  </div>
);

// ─── CLIENTS VIEW ───
const ClientsView = () => {
  const [search, setSearch] = useState("");
  const filtered = clients.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.contact.toLowerCase().includes(search.toLowerCase()) ||
    c.sector.toLowerCase().includes(search.toLowerCase())
  );

  const statusColors = {
    Activo: "bg-emerald-500/20 text-emerald-400",
    Nuevo: "bg-[#3b82f6]/20 text-[#3b82f6]",
    Pendiente: "bg-amber-500/20 text-amber-400",
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-white text-lg sm:text-xl font-bold">Gestión de Clientes</h2>
          <p className="text-[#94a3b8] text-sm">{clients.length} clientes registrados</p>
        </div>
        <motion.button
          className="bg-[#3b82f6] text-white text-sm font-medium px-4 py-2 flex items-center gap-2 w-full sm:w-auto justify-center"
          whileHover={{ boxShadow: "0 0 20px rgba(59,130,246,0.4)" }}
          whileTap={{ scale: 0.97 }}
          data-testid="add-client-btn"
        >
          <Plus className="w-4 h-4" /> Nuevo Cliente
        </motion.button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94a3b8]" />
          <input
            data-testid="client-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre, contacto o sector..."
            className="w-full bg-[#0b1221] border border-white/10 pl-10 pr-4 py-2.5 text-white text-sm placeholder:text-[#94a3b8]/50 focus:border-[#3b82f6] focus:outline-none transition-colors"
          />
        </div>
        <button className="bg-[#0b1221] border border-white/10 px-4 py-2 text-[#94a3b8] hover:text-white transition-colors flex items-center gap-2 text-sm justify-center">
          <Filter className="w-4 h-4" /> Filtrar
        </button>
      </div>

      {/* Mobile card view */}
      <div className="sm:hidden space-y-3" data-testid="clients-cards-mobile">
        {filtered.map((client, i) => (
          <motion.div
            key={client.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: i * 0.05 }}
            className="glass-card p-4 space-y-3"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-[#3b82f6]/10 flex items-center justify-center text-[#3b82f6] text-xs font-bold flex-shrink-0">
                  {client.name.charAt(0)}
                </div>
                <div className="min-w-0">
                  <p className="text-white text-sm font-medium truncate">{client.name}</p>
                  <p className="text-[#94a3b8] text-xs truncate">{client.contact}</p>
                </div>
              </div>
              <span className={`text-xs px-2 py-1 font-medium flex-shrink-0 ${statusColors[client.status]}`}>
                {client.status}
              </span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-[#94a3b8]">{client.sector}</span>
              <span className="text-white font-medium">{client.revenue}</span>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Desktop table view */}
      <div className="glass-card overflow-hidden hidden sm:block">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="clients-table">
            <thead>
              <tr className="border-b border-white/5">
                <th className="text-left py-3 px-4 text-[#94a3b8] text-xs font-medium uppercase tracking-wider">Empresa</th>
                <th className="text-left py-3 px-4 text-[#94a3b8] text-xs font-medium uppercase tracking-wider hidden md:table-cell">Contacto</th>
                <th className="text-left py-3 px-4 text-[#94a3b8] text-xs font-medium uppercase tracking-wider">Sector</th>
                <th className="text-left py-3 px-4 text-[#94a3b8] text-xs font-medium uppercase tracking-wider">Estado</th>
                <th className="text-left py-3 px-4 text-[#94a3b8] text-xs font-medium uppercase tracking-wider">Ingresos</th>
                <th className="text-left py-3 px-4 text-[#94a3b8] text-xs font-medium uppercase tracking-wider"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((client, i) => (
                <motion.tr
                  key={client.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: i * 0.05 }}
                  className="border-b border-white/5 hover:bg-white/5 transition-colors group"
                >
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-[#3b82f6]/10 flex items-center justify-center text-[#3b82f6] text-xs font-bold">
                        {client.name.charAt(0)}
                      </div>
                      <span className="text-white text-sm font-medium">{client.name}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4 hidden md:table-cell">
                    <p className="text-white text-sm">{client.contact}</p>
                    <p className="text-[#94a3b8] text-xs">{client.email}</p>
                  </td>
                  <td className="py-3 px-4 text-[#94a3b8] text-sm">{client.sector}</td>
                  <td className="py-3 px-4">
                    <span className={`text-xs px-2 py-1 font-medium ${statusColors[client.status]}`}>
                      {client.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-white text-sm font-medium">{client.revenue}</td>
                  <td className="py-3 px-4">
                    <button className="text-[#94a3b8] hover:text-white opacity-0 group-hover:opacity-100 transition-all">
                      <MoreHorizontal className="w-4 h-4" />
                    </button>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </motion.div>
  );
};

// ─── INVENTORY VIEW ───
const InventoryView = () => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
      <div>
        <h2 className="text-white text-lg sm:text-xl font-bold">Inventario de Productos</h2>
        <p className="text-[#94a3b8] text-sm">{products.length} productos activos</p>
      </div>
      <motion.button
        className="bg-[#3b82f6] text-white text-sm font-medium px-4 py-2 flex items-center gap-2 w-full sm:w-auto justify-center"
        whileHover={{ boxShadow: "0 0 20px rgba(59,130,246,0.4)" }}
        data-testid="add-product-btn"
      >
        <Plus className="w-4 h-4" /> Nuevo Producto
      </motion.button>
    </div>

    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="products-grid">
      {products.map((product, i) => {
        const stockPercent = Math.round((product.stock / product.total) * 100);
        const stockColor = stockPercent > 60 ? "#10b981" : stockPercent > 30 ? "#f59e0b" : "#ef4444";
        return (
          <motion.div
            key={product.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.08 }}
            className="glass-card p-5 group"
            whileHover={{ borderColor: `${stockColor}40`, y: -3 }}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-white font-semibold text-sm">{product.name}</h3>
                <span className="text-[#94a3b8] text-xs">{product.category}</span>
              </div>
              <span className="text-white font-bold text-sm">{product.price}</span>
            </div>

            <div className="mb-2">
              <div className="flex justify-between text-xs mb-1">
                <span className="text-[#94a3b8]">Stock</span>
                <span style={{ color: stockColor }} className="font-medium">{product.stock}/{product.total}</span>
              </div>
              <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                <motion.div
                  className="h-full rounded-full"
                  style={{ backgroundColor: stockColor }}
                  initial={{ width: 0 }}
                  animate={{ width: `${stockPercent}%` }}
                  transition={{ delay: 0.5 + i * 0.1, duration: 0.8 }}
                />
              </div>
            </div>

            <div className={`flex items-center gap-1 text-xs ${product.trend === "up" ? "text-emerald-400" : "text-red-400"}`}>
              {product.trend === "up" ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {product.trend === "up" ? "Demanda alta" : "Reabastecer pronto"}
            </div>
          </motion.div>
        );
      })}
    </div>
  </motion.div>
);

// ─── PLACEHOLDER VIEW ───
const PlaceholderView = ({ title, desc }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    className="flex flex-col items-center justify-center min-h-[400px] glass-card"
  >
    <Zap className="w-12 h-12 text-[#3b82f6]/30 mb-4" />
    <h3 className="text-white font-semibold text-lg mb-2">{title}</h3>
    <p className="text-[#94a3b8] text-sm max-w-sm text-center">{desc}</p>
    <Link to="/#precios">
      <motion.button
        className="mt-6 bg-[#3b82f6] text-white text-sm px-6 py-2 font-medium"
        whileHover={{ boxShadow: "0 0 20px rgba(59,130,246,0.4)" }}
      >
        Desbloquear con Plan Pro
      </motion.button>
    </Link>
  </motion.div>
);

// ─── LEAD CAPTURE FORM ───
const LeadCaptureForm = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({ name: "", email: "", company: "", phone: "", interest: "general" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/leads`, { ...formData, source: "demo_tour" });
      setIsSuccess(true);
      toast.success("¡Gracias por tu interés! Te contactaremos pronto.");
    } catch {
      toast.error("Error al enviar. Inténtalo de nuevo.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 30 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ type: "spring", bounce: 0.3 }}
        className="bg-[#0b1221] border border-[#3b82f6]/30 w-full max-w-md relative overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="lead-capture-modal"
      >
        {/* Animated top border */}
        <motion.div
          className="absolute top-0 left-0 w-full h-1"
          style={{ background: "linear-gradient(90deg, #3b82f6, #06b6d4, #8b5cf6, #3b82f6)", backgroundSize: "300% 100%" }}
          animate={{ backgroundPosition: ["0% 0%", "300% 0%"] }}
          transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
        />

        <button onClick={onClose} className="absolute top-4 right-4 text-[#94a3b8] hover:text-white z-10">
          <X className="w-5 h-5" />
        </button>

        {isSuccess ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="p-10 text-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", bounce: 0.5 }}
              className="w-20 h-20 bg-emerald-500/20 flex items-center justify-center mx-auto mb-6 rounded-full"
            >
              <CheckCircle className="w-10 h-10 text-emerald-400" />
            </motion.div>
            <h3 className="text-white font-bold text-2xl mb-3">¡Excelente!</h3>
            <p className="text-[#94a3b8] mb-6">Un especialista se pondrá en contacto contigo en las próximas 24 horas para darte una demostración personalizada.</p>
            <motion.button
              onClick={onClose}
              className="bg-[#3b82f6] text-white font-medium px-8 py-2.5"
              whileHover={{ boxShadow: "0 0 25px rgba(59,130,246,0.4)" }}
              data-testid="lead-success-close"
            >
              Continuar Explorando
            </motion.button>
          </motion.div>
        ) : (
          <div className="p-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-[#3b82f6]/20 flex items-center justify-center">
                <Rocket className="w-5 h-5 text-[#3b82f6]" />
              </div>
              <div>
                <h3 className="text-white font-bold text-lg">¡Has completado el tour!</h3>
                <p className="text-[#94a3b8] text-xs">Solicita una demostración personalizada</p>
              </div>
            </div>

            <p className="text-[#94a3b8] text-sm mb-6 mt-4">
              Déjanos tus datos y un especialista te contactará para mostrarte cómo Global Metafora puede transformar tu negocio.
            </p>

            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[#94a3b8] text-xs mb-1">Nombre *</label>
                  <input
                    name="name" value={formData.name} onChange={handleChange} required
                    data-testid="lead-input-name"
                    className="w-full bg-[#02040a] border border-white/10 px-3 py-2 text-white text-sm focus:border-[#3b82f6] focus:outline-none transition-colors placeholder:text-[#94a3b8]/40"
                    placeholder="Tu nombre"
                  />
                </div>
                <div>
                  <label className="block text-[#94a3b8] text-xs mb-1">Email *</label>
                  <input
                    name="email" type="email" value={formData.email} onChange={handleChange} required
                    data-testid="lead-input-email"
                    className="w-full bg-[#02040a] border border-white/10 px-3 py-2 text-white text-sm focus:border-[#3b82f6] focus:outline-none transition-colors placeholder:text-[#94a3b8]/40"
                    placeholder="tu@email.com"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[#94a3b8] text-xs mb-1">Empresa</label>
                  <input
                    name="company" value={formData.company} onChange={handleChange}
                    data-testid="lead-input-company"
                    className="w-full bg-[#02040a] border border-white/10 px-3 py-2 text-white text-sm focus:border-[#3b82f6] focus:outline-none transition-colors placeholder:text-[#94a3b8]/40"
                    placeholder="Tu empresa"
                  />
                </div>
                <div>
                  <label className="block text-[#94a3b8] text-xs mb-1">Teléfono</label>
                  <input
                    name="phone" value={formData.phone} onChange={handleChange}
                    data-testid="lead-input-phone"
                    className="w-full bg-[#02040a] border border-white/10 px-3 py-2 text-white text-sm focus:border-[#3b82f6] focus:outline-none transition-colors placeholder:text-[#94a3b8]/40"
                    placeholder="+1 (555) 000-0000"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[#94a3b8] text-xs mb-1">¿Qué te interesa más?</label>
                <select
                  name="interest" value={formData.interest} onChange={handleChange}
                  data-testid="lead-input-interest"
                  className="w-full bg-[#02040a] border border-white/10 px-3 py-2 text-white text-sm focus:border-[#3b82f6] focus:outline-none transition-colors"
                >
                  <option value="general">Demostración general</option>
                  <option value="erp">Sistema ERP completo</option>
                  <option value="crm">Gestión de clientes (CRM)</option>
                  <option value="inventory">Control de inventarios</option>
                  <option value="billing">Facturación electrónica</option>
                  <option value="custom">Desarrollo a medida</option>
                </select>
              </div>

              <motion.button
                type="submit"
                disabled={isSubmitting}
                data-testid="lead-submit-btn"
                className="w-full bg-[#3b82f6] text-white font-medium py-2.5 flex items-center justify-center gap-2 mt-4 relative overflow-hidden"
                whileHover={{ boxShadow: "0 0 30px rgba(59,130,246,0.4)" }}
                whileTap={{ scale: 0.98 }}
              >
                <motion.span
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent"
                  initial={{ x: "-100%" }}
                  whileHover={{ x: "100%" }}
                  transition={{ duration: 0.5 }}
                />
                <span className="relative flex items-center gap-2">
                  {isSubmitting ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Enviando...</>
                  ) : (
                    <><Send className="w-4 h-4" /> Solicitar Demostración Gratuita</>
                  )}
                </span>
              </motion.button>

              <p className="text-[#94a3b8] text-[10px] text-center mt-2">
                Sin compromiso. Respuesta en menos de 24 horas.
              </p>
            </form>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

// ─── GUIDED TOUR ───
const GuidedTour = ({ isActive, step, onNext, onClose, onComplete }) => {
  if (!isActive || step >= tourSteps.length) return null;
  const current = tourSteps[step];
  const isLastStep = step === tourSteps.length - 1;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="bg-[#0b1221] border border-[#3b82f6]/30 p-6 max-w-md mx-4 relative"
          onClick={(e) => e.stopPropagation()}
          data-testid="tour-modal"
        >
          <button onClick={onClose} className="absolute top-3 right-3 text-[#94a3b8] hover:text-white">
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[#3b82f6] text-xs font-semibold">PASO {step + 1}/{tourSteps.length}</span>
          </div>
          <h3 className="text-white font-bold text-lg mb-2">{current.title}</h3>
          <p className="text-[#94a3b8] text-sm mb-5 leading-relaxed">{current.desc}</p>
          
          {/* Progress dots */}
          <div className="flex items-center gap-2 mb-4">
            {tourSteps.map((s, i) => (
              <div key={s.title} className={`h-1 rounded-full flex-1 ${i <= step ? "bg-[#3b82f6]" : "bg-white/10"}`} />
            ))}
          </div>

          <div className="flex justify-between">
            <button onClick={onClose} className="text-[#94a3b8] text-sm hover:text-white transition-colors">
              Saltar tour
            </button>
            <motion.button
              onClick={isLastStep ? onComplete : onNext}
              className="bg-[#3b82f6] text-white text-sm font-medium px-5 py-2 flex items-center gap-2"
              whileHover={{ boxShadow: "0 0 20px rgba(59,130,246,0.4)" }}
              data-testid="tour-next-btn"
            >
              {isLastStep ? "Finalizar" : "Siguiente"}
              <ChevronRight className="w-4 h-4" />
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// ─── MAIN DEMO PAGE ───
const DemoPage = () => {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [showNotifications, setShowNotifications] = useState(false);
  const [tourActive, setTourActive] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const [showTourPrompt, setShowTourPrompt] = useState(true);
  const [showLeadCapture, setShowLeadCapture] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const tabTitles = {
    dashboard: "Dashboard",
    clients: "Clientes",
    inventory: "Inventario",
    invoices: "Facturación",
    reports: "Reportes",
    settings: "Configuración",
  };

  const startTour = () => {
    setShowTourPrompt(false);
    setTourStep(0);
    setTourActive(true);
    setActiveTab("dashboard");
  };

  const nextTourStep = () => {
    if (tourStep < tourSteps.length - 1) {
      setTourStep(tourStep + 1);
    } else {
      setTourActive(false);
    }
  };

  const completeTour = () => {
    setTourActive(false);
    setShowLeadCapture(true);
  };

  return (
    <div data-testid="demo-page" className="min-h-screen bg-[#02040a] flex">
      <SEO
        title="Demo Interactivo"
        description="Explora el dashboard interactivo de Global Metafora APP. Visualiza KPIs, gráficas, gestión de clientes e inventarios en tiempo real."
        path="/demo"
      />
      <ScrollProgress />
      <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />

      <div className="flex-1 flex flex-col min-h-screen min-w-0">
        {/* Top Bar */}
        <motion.header
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="h-14 sm:h-16 bg-[#02040a]/80 backdrop-blur-lg border-b border-white/5 flex items-center justify-between px-3 sm:px-6 sticky top-0 z-30"
        >
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileOpen(true)}
              className="lg:hidden text-[#94a3b8] hover:text-white transition-colors"
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-5 h-5" />
            </button>
            <Link to="/" data-testid="back-to-home" className="text-[#94a3b8] hover:text-white transition-colors hidden sm:block">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="text-white font-semibold text-sm sm:text-base">{tabTitles[activeTab]}</h1>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <motion.button
              onClick={startTour}
              className="text-[#3b82f6] text-xs sm:text-sm font-medium flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 border border-[#3b82f6]/30 hover:bg-[#3b82f6]/10 transition-colors"
              whileHover={{ scale: 1.02 }}
              data-testid="start-tour-btn"
            >
              <Play className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
              <span className="hidden xs:inline">Tour</span>
              <span className="hidden sm:inline"> Guiado</span>
            </motion.button>

            <div className="relative">
              <motion.button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center text-[#94a3b8] hover:text-white transition-colors"
                whileTap={{ scale: 0.9 }}
                data-testid="notifications-btn"
              >
                <Bell className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
              </motion.button>

              <AnimatePresence>
                {showNotifications && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    className="absolute right-0 top-12 w-72 sm:w-80 bg-[#0b1221] border border-white/10 z-50 shadow-2xl"
                    data-testid="notifications-panel"
                  >
                    <div className="p-4 border-b border-white/5">
                      <h3 className="text-white font-semibold text-sm">Notificaciones</h3>
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      {notifications.map((n) => (
                        <div key={n.id} className="p-3 border-b border-white/5 hover:bg-white/5 transition-colors">
                          <p className="text-white text-sm">{n.text}</p>
                          <p className="text-[#94a3b8] text-xs mt-1">{n.time}</p>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="flex items-center gap-2 pl-2 sm:pl-3 border-l border-white/10">
              <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-br from-[#3b82f6] to-[#06b6d4] flex items-center justify-center text-white text-[10px] sm:text-xs font-bold rounded-sm">
                EA
              </div>
              <div className="hidden md:block">
                <p className="text-white text-xs font-medium">Eriberto A.</p>
                <p className="text-[#94a3b8] text-[10px]">Admin</p>
              </div>
            </div>
          </div>
        </motion.header>

        {/* Tour Prompt */}
        <AnimatePresence>
          {showTourPrompt && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mx-3 sm:mx-6 mt-3 sm:mt-4 bg-gradient-to-r from-[#3b82f6]/10 to-[#06b6d4]/10 border border-[#3b82f6]/20 p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
              data-testid="tour-prompt"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 sm:w-10 sm:h-10 bg-[#3b82f6]/20 flex items-center justify-center flex-shrink-0">
                  <Eye className="w-4 h-4 sm:w-5 sm:h-5 text-[#3b82f6]" />
                </div>
                <div className="min-w-0">
                  <p className="text-white text-sm font-medium">Bienvenido al Demo</p>
                  <p className="text-[#94a3b8] text-xs">Inicia el tour guiado para conocer todas las funcionalidades</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => setShowTourPrompt(false)}
                  className="text-[#94a3b8] text-sm hover:text-white px-3 py-1.5"
                >
                  Omitir
                </button>
                <motion.button
                  onClick={startTour}
                  className="bg-[#3b82f6] text-white text-sm font-medium px-4 py-1.5 flex items-center gap-2"
                  whileHover={{ boxShadow: "0 0 20px rgba(59,130,246,0.4)" }}
                >
                  <Play className="w-3.5 h-3.5" /> Iniciar
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Content */}
        <main className="flex-1 p-3 sm:p-6 pb-20 lg:pb-6">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {activeTab === "dashboard" && <DashboardView />}
              {activeTab === "clients" && <ClientsView />}
              {activeTab === "inventory" && <InventoryView />}
              {activeTab === "invoices" && (
                <PlaceholderView
                  title="Módulo de Facturación"
                  desc="Genera facturas electrónicas, controla pagos y gestiona cobros. Disponible en el plan Pro."
                />
              )}
              {activeTab === "reports" && (
                <PlaceholderView
                  title="Centro de Reportes"
                  desc="Reportes avanzados, exportación a PDF/Excel, y análisis predictivo. Disponible en el plan Pro."
                />
              )}
              {activeTab === "settings" && (
                <PlaceholderView
                  title="Configuración del Sistema"
                  desc="Personaliza tu sistema operativo, gestiona usuarios y permisos. Disponible en todos los planes."
                />
              )}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      <nav
        className="fixed bottom-0 left-0 right-0 bg-[#0a0f1a]/95 backdrop-blur-lg border-t border-white/10 z-40 lg:hidden"
        data-testid="mobile-bottom-nav"
      >
        <div className="flex items-center justify-around h-14">
          {[
            { id: "dashboard", icon: LayoutDashboard, label: "Inicio" },
            { id: "clients", icon: Users, label: "Clientes" },
            { id: "inventory", icon: Package, label: "Inventario" },
            { id: "invoices", icon: FileText, label: "Facturas" },
            { id: "reports", icon: BarChart3, label: "Reportes" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`bottom-nav-${tab.id}`}
              className={`flex flex-col items-center justify-center gap-0.5 flex-1 h-full transition-colors relative ${
                activeTab === tab.id
                  ? "text-[#3b82f6]"
                  : "text-[#94a3b8] active:text-white"
              }`}
            >
              {activeTab === tab.id && (
                <motion.div
                  layoutId="bottomNavIndicator"
                  className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-[#3b82f6] rounded-full"
                  transition={{ type: "spring", stiffness: 400, damping: 30 }}
                />
              )}
              <tab.icon className="w-5 h-5" />
              <span className="text-[9px] font-medium">{tab.label}</span>
            </button>
          ))}
        </div>
      </nav>

      {/* Guided Tour */}
      <GuidedTour
        isActive={tourActive}
        step={tourStep}
        onNext={nextTourStep}
        onClose={() => setTourActive(false)}
        onComplete={completeTour}
      />

      {/* Lead Capture Form */}
      <AnimatePresence>
        <LeadCaptureForm
          isOpen={showLeadCapture}
          onClose={() => setShowLeadCapture(false)}
        />
      </AnimatePresence>
    </div>
  );
};

export default DemoPage;
